import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';

/**
 * 363 (#1090, design 2i): where this car stands. Hosts compete for the top of the customer search, so the card
 * states the rule in one line, and links to this car's reviews (the list opens filtered on it).
 */
export function CarRatingCard({
  carId,
  rating,
}: {
  carId: string;
  rating: { average: number; count: number } | null | undefined;
}) {
  return (
    <Card>
      <div data-testid="car-rating-card" className="flex flex-col gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-steel">
          Rating
        </span>
        {rating ? (
          <>
            <div className="flex items-center gap-2.5">
              <span className="text-2xl font-bold text-ink">{rating.average.toFixed(1)}</span>
              <HalfStars value={rating.average} />
              <span className="text-sm text-slate">
                {rating.count} {rating.count === 1 ? 'review' : 'reviews'}
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-slate">No reviews yet.</p>
        )}
        <p className="text-sm text-slate">
          Search orders cars by rating; a car with few reviews counts as nearer the average.
        </p>
        {rating && (
          <Link to={`/reviews?car_id=${carId}`} className="text-sm font-medium text-primary">
            See this car's reviews
          </Link>
        )}
      </div>
    </Card>
  );
}

/** Five stars to the nearest half, like the customer app's (4.6 shows 4½). */
function HalfStars({ value }: { value: number }) {
  const halves = Math.round(value * 2) / 2;
  return (
    <span
      className="inline-flex text-base leading-none"
      role="img"
      aria-label={`${value} out of 5`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const fill = n <= halves ? 'full' : n - 0.5 === halves ? 'half' : 'empty';
        return (
          <span key={n} data-fill={fill} className="relative inline-block text-hairline-strong">
            ★
            {fill !== 'empty' && (
              <span
                className="absolute inset-y-0 left-0 overflow-hidden text-semantic-warning-text"
                style={{ width: fill === 'half' ? '50%' : '100%' }}
              >
                ★
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}
